import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import axios from 'utils/axios';
import axiosAdmin from 'utils/axiosAdmin';
import URL from 'config/url';
import { buildHotelBookingsQuery } from 'reducer/hotel';
import { buildResortBookingsQuery } from 'reducer/resort';
import { buildFlightBookingsQuery } from 'reducer/flight';
import { buildCarBookingsQuery } from 'reducer/car';
import { buildActivityBookingsQuery } from 'reducer/activity';

const downloadBlobAsFile = (blob, filename) => {
  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(downloadUrl);
};

const handleCSVExportResponse = (response, defaultFilename) => {
  const responseData = response.data;
  const csvExport = responseData.data;
  
  if (responseData.status === 'success' && csvExport?.csv_data) {
    const contentType = csvExport.content_type || 'text/csv; charset=utf-8';
    const blob = new Blob([csvExport.csv_data], { type: contentType });
    const filename = csvExport.file_name || defaultFilename;
    return { blob, filename };
  } else {
    throw new Error(responseData.message || 'CSV export failed');
  }
};

export const getCSVData = createAsyncThunk('GET CSV DATA', async (data, thunkAPI) => {
  let query = `?page=${data?.page}&pageSize=${data?.pageSize}&limit=${data?.limit}&text=${data?.text}&bookingStatus=${data?.bookingStatus}&provider=${data?.provider}&refundability=${data.refundability}&currency=${data.currency}&paymentGateway=${data.paymentGateway}&startDate=${data?.startDate}&endDate=${data?.endDate}&category=${data?.category}&sorting=${data?.sorting}&userType=${data?.userType}&travelStartDate=${data.travelStartDate}&travelEndDate=${data.travelEndDate}&agencyName=${data?.agencyName}`;
  if (data?.agencyId) {
    query = `?agencyId=${data?.agencyId}&page=${data?.page}&pageSize=${data?.pageSize}&limit=${data?.limit}&text=${data?.text}&bookingStatus=${data?.bookingStatus}&provider=${data?.provider}&refundability=${data?.refundability}&currency=${data.currency}&paymentGateway=${data.paymentGateway}&startDate=${data?.startDate}&endDate=${data?.endDate}&category=${data?.category}&sorting=${data?.sorting}&userType=${data?.userType}&travelStartDate=${data.travelStartDate}&travelEndDate=${data.travelEndDate}&agencyName=${data?.agencyName}`;
  }
  const response = await axios.get(`${URL.CSV_DOWNLOAD}${query}`, thunkAPI);
  return response.data;
});

export const getCSVDataResort = createAsyncThunk('GET CSV DATA RESORT', async (data, thunkAPI) => {
  const query = buildResortBookingsQuery(data, { csv: true });
  const response = await axiosAdmin.get(`${URL.RESORT_BOOKINGS}?${query}`, thunkAPI);
  return handleCSVExportResponse(response, 'resort_bookings.csv');
});

export const getCSVDataHotel = createAsyncThunk('GET CSV DATA HOTEL', async (data, thunkAPI) => {
  const query = buildHotelBookingsQuery(data, { csv: true });
  const response = await axiosAdmin.get(`${URL.HOTEL}?${query}`, thunkAPI);
  return handleCSVExportResponse(response, 'hotel_bookings.csv');
});

export const getCSVDataFlight = createAsyncThunk('GET CSV DATA FLIGHT', async (data, thunkAPI) => {
  const query = buildFlightBookingsQuery(data, { csv: true });
  const response = await axiosAdmin.get(`${URL.FLIGHT}?${query}`, thunkAPI);
  return handleCSVExportResponse(response, 'flight_bookings.csv');
});

export const getCSVDataCar = createAsyncThunk('GET CSV DATA CAR', async (data, thunkAPI) => {
  const query = buildCarBookingsQuery(data, { csv: true });
  const response = await axiosAdmin.get(`${URL.CAR_V2}?${query}`, thunkAPI);
  return handleCSVExportResponse(response, 'car_bookings.csv');
});

export const getCSVDataActivity = createAsyncThunk('GET CSV DATA ACTIVITY', async (data, thunkAPI) => {
  const query = buildActivityBookingsQuery(data, { csv: true });
  const response = await axiosAdmin.get(`${URL.ACTIVITY_V2}?${query}`, thunkAPI);
  return handleCSVExportResponse(response, 'activity_bookings.csv');
});

const formatAccountsQueryParam = (value) => {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value.length > 0 ? value.join(',') : '';
  return value;
};

export const buildAccountsQuery = (data, options = {}) => {
  const { csv = false } = options;
  const params = new URLSearchParams();

  if (!csv) {
    params.set('page', String(data.page || 1));
    params.set('limit', String(data.limit || 10));
  }
  if (csv) params.set('csv', 'true');

  params.set('text', data.text || '');
  params.set('startDate', data.startDate || '');
  params.set('endDate', data.endDate || '');
  params.set('planType', formatAccountsQueryParam(data.planType));
  params.set('solutionType', formatAccountsQueryParam(data.solutionType));
  params.set('subscriptionStatus', formatAccountsQueryParam(data.subscriptionStatus));
  params.set('sortField', data.sortField || '');
  params.set('sortOrder', data.sortOrder || '');
  params.set('paymentStatus', formatAccountsQueryParam(data.paymentStatus));
  params.set('accountStatus', formatAccountsQueryParam(data.accountStatus ?? data.agencyStatus));
  params.set('accountName', formatAccountsQueryParam(data.accountName ?? data.agencyName));
  params.set('originCountry', formatAccountsQueryParam(data.originCountry));
  params.set('currentGateway', formatAccountsQueryParam(data.currentGateway ?? data.paymentGateway));
  params.set('preferredGateway', formatAccountsQueryParam(data.preferredGateway));
  params.set('lastGateway', formatAccountsQueryParam(data.lastGateway));
  params.set('current3ds', formatAccountsQueryParam(data.current3ds));
  params.set('preferred3ds', formatAccountsQueryParam(data.preferred3ds));
  params.set('last3ds', formatAccountsQueryParam(data.last3ds));
  params.set('isSSOEnabled', formatAccountsQueryParam(data.isSSOEnabled));
  params.set('isNonLogin', formatAccountsQueryParam(data.isNonLogin));
  params.set('hotelVendor', formatAccountsQueryParam(data.hotelVendor));
  params.set('classification', formatAccountsQueryParam(data.classification));

  return params.toString();
};

export const getAgencyCSVData = createAsyncThunk('GET AGENCY CSV DATA', async (data, thunkAPI) => {
  const query = buildAccountsQuery(data, { csv: true });
  const response = await axiosAdmin.get(`${URL.GET_ACCOUNT_AGENCIES}?${query}`, thunkAPI);
  return handleCSVExportResponse(response, 'accounts.csv');
});

export const getSearchesCSVData = createAsyncThunk('GET SEARCHES CSV DATA', async (data, thunkAPI) => {
  const query = `?text=${data?.text}&startDate=${data?.startDate}&endDate=${data?.endDate}`;

  const response = await axios.get(`${URL.SEARCHES_CSV_DOWNLOAD}${query}`, thunkAPI);
  return response.data;
});

const initialState = {
  isLoading: false,
  csvData: null,
};

export const CSVSlice = createSlice({
  name: 'csv',
  initialState,
  reducers: {},
  extraReducers: {
    // get csv data
    [getCSVData.pending]: (state) => {
      state.isLoading = true;
      state.csvData = null;
    },
    [getCSVData.fulfilled]: (state, action) => {
      state.isLoading = false;
      state.csvData = action.payload.data;
    },
    [getCSVData.rejected]: (state, action) => {
      state.isLoading = false;
      state.csvData = null;
      toast.error(action?.payload?.message);
    },

    // get csv data resort
    [getCSVDataResort.pending]: (state) => {
      state.isLoading = true;
    },
    [getCSVDataResort.fulfilled]: (state, action) => {
      state.isLoading = false;
      const { blob, filename } = action.payload;
      downloadBlobAsFile(blob, filename);
      toast.success('CSV downloaded successfully');
    },
    [getCSVDataResort.rejected]: (state, action) => {
      state.isLoading = false;
      toast.error(action?.payload?.message || 'Failed to download CSV');
    },

    // get csv data hotel
    [getCSVDataHotel.pending]: (state) => {
      state.isLoading = true;
    },
    [getCSVDataHotel.fulfilled]: (state, action) => {
      state.isLoading = false;
      const { blob, filename } = action.payload;
      downloadBlobAsFile(blob, filename);
      toast.success('CSV downloaded successfully');
    },
    [getCSVDataHotel.rejected]: (state, action) => {
      state.isLoading = false;
      toast.error(action?.payload?.message || 'Failed to download CSV');
    },

    // get csv data flight
    [getCSVDataFlight.pending]: (state) => {
      state.isLoading = true;
    },
    [getCSVDataFlight.fulfilled]: (state, action) => {
      state.isLoading = false;
      const { blob, filename } = action.payload;
      downloadBlobAsFile(blob, filename);
      toast.success('CSV downloaded successfully');
    },
    [getCSVDataFlight.rejected]: (state, action) => {
      state.isLoading = false;
      toast.error(action?.payload?.message || 'Failed to download CSV');
    },

    // get csv data car
    [getCSVDataCar.pending]: (state) => {
      state.isLoading = true;
    },
    [getCSVDataCar.fulfilled]: (state, action) => {
      state.isLoading = false;
      const { blob, filename } = action.payload;
      downloadBlobAsFile(blob, filename);
      toast.success('CSV downloaded successfully');
    },
    [getCSVDataCar.rejected]: (state, action) => {
      state.isLoading = false;
      toast.error(action?.payload?.message || 'Failed to download CSV');
    },

    // get csv data activity
    [getCSVDataActivity.pending]: (state) => {
      state.isLoading = true;
    },
    [getCSVDataActivity.fulfilled]: (state, action) => {
      state.isLoading = false;
      const { blob, filename } = action.payload;
      downloadBlobAsFile(blob, filename);
      toast.success('CSV downloaded successfully');
    },
    [getCSVDataActivity.rejected]: (state, action) => {
      state.isLoading = false;
      toast.error(action?.payload?.message || 'Failed to download CSV');
    },

    // get agency csv data
    [getAgencyCSVData.pending]: (state) => {
      state.isLoading = true;
    },
    [getAgencyCSVData.fulfilled]: (state, action) => {
      state.isLoading = false;
      const { blob, filename } = action.payload;
      downloadBlobAsFile(blob, filename);
      toast.success('CSV downloaded successfully');
    },
    [getAgencyCSVData.rejected]: (state, action) => {
      state.isLoading = false;
      toast.error(action?.payload?.message || 'Failed to download CSV');
    },

    // get searches csv data
    [getSearchesCSVData.pending]: (state) => {
      state.isLoading = true;
      state.csvData = null;
    },
    [getSearchesCSVData.fulfilled]: (state, action) => {
      state.isLoading = false;
      state.csvData = action.payload.data;
    },
    [getSearchesCSVData.rejected]: (state, action) => {
      state.isLoading = false;
      state.csvData = null;
      toast.error(action?.payload?.message);
    },
  },
});

export default CSVSlice.reducer;
